/**
 * Block-level copy/paste (whole blocks, not just their text).
 *
 * Copied blocks travel on the clipboard as HTML carrying the full block
 * JSON (an array — single copies are a one-element array) in a data
 * attribute, with readable text alongside — so pasting into the editor
 * recreates the blocks exactly (type, props, children, colors, icons),
 * while pasting into an external app gives plain text.
 */

import { generateId } from './blockUtils';
import { cellToInlineHtml } from './tableCellBlocks';
import { getNumberedListIndex } from './indentUtils';
import { inlineHtmlToMarkdown } from './pasteUtils';
import { expandBraceVarsInText, refreshVariableHtml } from './variableUtils';

export const BLOCK_CLIPBOARD_ATTR = 'data-doc-block';

/** Deep-clone a block giving every node a fresh id (paste = new identity). */
export const cloneBlockWithNewIds = (block) => ({
  ...block,
  id: generateId(),
  children: (block.children || []).map(cloneBlockWithNewIds),
});

/**
 * Readable plain-text version of blocks (for pasting outside the app).
 * Bullets/numbers/checkboxes keep their markdown markers so a text-only paste
 * can still be recognized and converted back into those block types. Quote
 * callouts emit "> " lines (markdown blockquote — not "|", which is tables).
 * Inline <code>/<b>/<i> become markdown so formatting survives text/plain pastes.
 */
const inlineToText = (html, { variablesAsValues = false, resolveVariable } = {}) => {
  const source =
    resolveVariable && variablesAsValues
      ? refreshVariableHtml(html || '', resolveVariable)
      : html || '';
  return inlineHtmlToMarkdown(source, { variablesAsValues, resolveVariable }).trim();
};

const tableToMarkdownLines = (tableData, options) => {
  const headers = tableData?.headers || [];
  const rows = tableData?.rows || [];
  if (!headers.length) return [];
  const fmt = (cells) =>
    `| ${cells.map((c) => inlineToText(cellToInlineHtml(c), options)).join(' | ')} |`;
  return [
    fmt(headers),
    `| ${headers.map(() => '---').join(' | ')} |`,
    ...rows.map((row) => fmt(row)),
  ];
};

const appendPlainTextLines = (list, lines, options = {}) => {
  const { inQuote = false } = options;
  const q = inQuote ? '> ' : '';
  list.forEach((b, i) => {
    if (b.type === 'table') {
      tableToMarkdownLines(b.props?.tableData, options).forEach((line) => lines.push(`${q}${line}`));
      return;
    }
    if (b.type === 'linked-variable-row') {
      const name = String(b.props?.variableName || '').trim();
      if (name) lines.push(`${q}{{${name}}}`);
      return;
    }
    // Code stores HTML (pills) — same inline path as text; also expand any
    // leftover plain {{tokens}} when copying values.
    if (b.type === 'code') {
      let text = inlineToText(b.content || '', options);
      if (options.variablesAsValues && options.resolveVariable) {
        text = expandBraceVarsInText(text, options.resolveVariable);
      }
      if (text) lines.push(`${q}${text}`);
      return;
    }
    const text = inlineToText(b.content || '', options);
    if (text) {
      if (b.type === 'bullet') lines.push(`${q}- ${text}`);
      else if (b.type === 'number') lines.push(`${q}${getNumberedListIndex(list, i)}. ${text}`);
      else if (b.type === 'checkbox') {
        const mark = b.props?.indeterminate ? '-' : b.props?.checked ? 'x' : ' ';
        lines.push(`${q}[${mark}] ${text}`);
      }
      else if (b.type === 'heading') lines.push(`${q}${'#'.repeat(b.props?.level || 1)} ${text}`);
      else if (b.type === 'choice') {
        lines.push(`${q}## ${text}`);
      }
      else if (b.type === 'choice-path') {
        /* path labels appended below */
      }
      else if (!['row', 'column', 'callout', 'toggle', 'choice', 'choice-path'].includes(b.type)) lines.push(`${q}${text}`);
    }
    if (b.type === 'choice-path') {
      const label = inlineToText(b.content || '', options);
      if (label) lines.push(`${q}### ${label}`);
      if (b.children?.length) appendPlainTextLines(b.children, lines, options);
      return;
    }
    if (b.children?.length) {
      const childInQuote = inQuote || (b.type === 'callout' && b.props?.variant === 'quote');
      appendPlainTextLines(b.children, lines, { ...options, inQuote: childInQuote });
    }
  });
};

/**
 * Readable plain-text version of blocks.
 * @param {object[]} blocks
 * @param {{
 *   variablesAsValues?: boolean,
 *   resolveVariable?: Function,
 *   blockSeparator?: string,
 * }} [options]
 *   variablesAsValues — copy resolved variable display text instead of {{name}}
 *   blockSeparator — join between blocks (default `\n`). Callout Copy uses
 *   `\n\n` so Enter (new block) is a blank line, while Shift+Enter (`<br>`
 *   inside a block) stays a single newline.
 */
export const blocksToPlainText = (blocks, options = {}) => {
  const lines = [];
  appendPlainTextLines(blocks || [], lines, options);
  return lines.join(options.blockSeparator ?? '\n');
};

/** Write one block or a list of blocks to the clipboard (JSON + text). */
export const copyBlocksToClipboard = async (blockOrBlocks) => {
  const blocks = Array.isArray(blockOrBlocks) ? blockOrBlocks : [blockOrBlocks];
  const { html, text } = blocksToClipboardPayload(blocks);

  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
    return true;
  }
  // Fallback: text only (no block structure, but nothing breaks)
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
  }
  return false;
};

const escapeCell = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** Visible HTML fallback (when a consumer ignores data-doc-block). */
const blocksToClipboardHtml = (list) =>
  (list || [])
    .map((b) => {
      if (b.type === 'callout' && b.props?.variant === 'quote') {
        const inner = blocksToClipboardHtml(b.children);
        return `<blockquote>${inner || '<p></p>'}</blockquote>`;
      }
      if (b.type === 'table') {
        const headers = b.props?.tableData?.headers || [];
        const rows = b.props?.tableData?.rows || [];
        if (!headers.length) return '';
        // Cells may already hold safe inline HTML (<b>, <code>, …)
        const cellHtml = (c) => {
          const raw = cellToInlineHtml(c);
          return /<[a-z][\s\S]*>/i.test(raw) ? raw : escapeCell(raw);
        };
        const head = headers.map((h) => `<th>${cellHtml(h)}</th>`).join('');
        const body = rows
          .map((row) => `<tr>${row.map((c) => `<td>${cellHtml(c)}</td>`).join('')}</tr>`)
          .join('');
        return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
      }
      if (['row', 'column', 'callout', 'toggle', 'choice', 'choice-path'].includes(b.type)) {
        return blocksToClipboardHtml(b.children);
      }
      const inner = (b.content || '').trim();
      if (!inner) return '';
      if (b.type === 'heading') {
        const level = Math.min(4, Math.max(1, b.props?.level || 1));
        return `<h${level}>${inner}</h${level}>`;
      }
      return `<p>${inner}</p>`;
    })
    .filter(Boolean)
    .join('');

const blocksToClipboardPayload = (blocks) => {
  const json = encodeURIComponent(JSON.stringify(blocks));
  const text = blocksToPlainText(blocks) || `[${blocks.map((b) => b.type).join(', ')}]`;
  // Keep real inline HTML in the visible markup too (not just the JSON), so
  // <code> / quotes survive even if a consumer ignores data-doc-block.
  const rich = blocksToClipboardHtml(blocks);
  const html = `<div ${BLOCK_CLIPBOARD_ATTR}="${json}">${rich || text.replace(/\n/g, '<br>')}</div>`;
  return { html, text };
};

/**
 * Synchronous copy that works from a keyboard shortcut with NO text
 * selection (the async Clipboard API can silently no-op there, and the
 * native copy event never fires without a selection/focused editable).
 * Selects a hidden node holding the payload and runs execCommand('copy') —
 * the browser serializes it to text/html + text/plain for us.
 */
export const copyBlocksViaExecCommand = (blockOrBlocks) => {
  const blocks = Array.isArray(blockOrBlocks) ? blockOrBlocks : [blockOrBlocks];
  const { html, text } = blocksToClipboardPayload(blocks);

  // The payload is injected through a one-shot copy listener rather than by
  // serializing a selected DOM node — browser serialization can rewrite the
  // HTML and drop the data attribute that carries the block JSON.
  let ok = false;
  const onCopy = (e) => {
    e.preventDefault();
    e.stopImmediatePropagation();
    e.clipboardData.setData('text/html', html);
    e.clipboardData.setData('text/plain', text);
    ok = true;
  };
  document.addEventListener('copy', onCopy, true);

  // execCommand('copy') only dispatches the event when something is
  // selected — select a hidden node for the duration
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;pointer-events:none;';
  host.textContent = 'x';
  document.body.appendChild(host);

  const selection = window.getSelection();
  const previous = [];
  for (let i = 0; i < (selection?.rangeCount || 0); i += 1) {
    previous.push(selection.getRangeAt(i).cloneRange());
  }

  try {
    const range = document.createRange();
    range.selectNodeContents(host);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand('copy');
  } catch {
    /* fall through — ok stays false */
  }

  document.removeEventListener('copy', onCopy, true);
  selection.removeAllRanges();
  previous.forEach((r) => selection.addRange(r));
  document.body.removeChild(host);
  return ok;
};

/**
 * Synchronous variant for native copy/cut events — writes through the
 * event's DataTransfer, which never needs async clipboard permissions.
 */
export const writeBlocksToClipboardData = (clipboardData, blockOrBlocks) => {
  if (!clipboardData) return false;
  const blocks = Array.isArray(blockOrBlocks) ? blockOrBlocks : [blockOrBlocks];
  const { html, text } = blocksToClipboardPayload(blocks);
  clipboardData.setData('text/html', html);
  clipboardData.setData('text/plain', text);
  return true;
};

/** Extract blocks from clipboard HTML, or null if it isn't a block copy. */
export const readBlocksFromClipboardHtml = (html) => {
  if (!html || !html.includes(BLOCK_CLIPBOARD_ATTR)) return null;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const host = doc.querySelector(`[${BLOCK_CLIPBOARD_ATTR}]`);
    if (!host) return null;
    const parsed = JSON.parse(decodeURIComponent(host.getAttribute(BLOCK_CLIPBOARD_ATTR)));
    const list = Array.isArray(parsed) ? parsed : [parsed];
    const valid = list.filter((b) => b && typeof b === 'object' && b.type);
    if (valid.length === 0) return null;
    return valid.map(cloneBlockWithNewIds);
  } catch {
    return null;
  }
};
